// AppOnboarding — first-time PWA/standalone launch slides.
// Rendered by <LoginPage/> when running in PWA mode + no prior onboarding flag.
// Once completed, we set localStorage["rostr.app_onboarded"] = "1" so subsequent
// launches skip straight to the login/create-account screen.
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarDays, Users, Bell, ArrowRight, ArrowLeft } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";

export const APP_ONBOARDED_KEY = "rostr.app_onboarded";

/** Returns true when the app is running as an installed PWA (standalone). */
export function isRunningAsApp() {
  if (typeof window === "undefined") return false;
  try {
    return (
      window.matchMedia?.("(display-mode: standalone)")?.matches === true ||
      // iOS Safari PWA
      window.navigator?.standalone === true
    );
  } catch {
    return false;
  }
}

/** Whether the onboarding slides should be shown right now. */
export function shouldShowOnboarding() {
  if (typeof window === "undefined") return false;
  if (!isRunningAsApp()) return false;
  try {
    return localStorage.getItem(APP_ONBOARDED_KEY) !== "1";
  } catch {
    return false;
  }
}

/** Mark onboarding as completed — safe to call multiple times. */
export function markOnboarded() {
  try { localStorage.setItem(APP_ONBOARDED_KEY, "1"); } catch { /* ignore */ }
}

const SLIDE_ICONS = [CalendarDays, Users, Bell];
const SLIDE_ACCENTS = [
  { from: "hsl(217 91% 60%)", to: "hsl(217 91% 45%)" },
  { from: "hsl(280 65% 60%)", to: "hsl(280 65% 45%)" },
  { from: "hsl(38 92% 55%)",  to: "hsl(28 92% 45%)"  },
];

export default function AppOnboarding({ onDone }) {
  const { t, lang, setLang, langs } = useI18n();
  const [step, setStep] = useState(0);

  const slides = [
    { titleKey: "app_onboard_1_title", descKey: "app_onboard_1_desc" },
    { titleKey: "app_onboard_2_title", descKey: "app_onboard_2_desc" },
    { titleKey: "app_onboard_3_title", descKey: "app_onboard_3_desc" },
  ];

  const isLast = step === slides.length - 1;
  const next = () => {
    if (isLast) {
      markOnboarded();
      onDone?.();
    } else {
      setStep((s) => s + 1);
    }
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const Icon = SLIDE_ICONS[step];
  const accent = SLIDE_ACCENTS[step];

  return (
    <div
      className="fixed inset-0 z-[9998] overflow-hidden"
      data-testid="app-onboarding"
      style={{
        minHeight: "100dvh",
        backgroundColor: "hsl(var(--bg))",
        backgroundImage: `
          radial-gradient(140% 90% at 20% 8%, ${accent.from}28 0%, transparent 55%),
          radial-gradient(110% 80% at 85% 18%, ${accent.to}20 0%, transparent 60%),
          radial-gradient(100% 70% at 50% 105%, hsl(280 65% 60% / 0.18) 0%, transparent 55%)
        `,
      }}
    >
      {/* Subtle noise texture */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.05] mix-blend-overlay"
        style={{ backgroundImage: "url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22200%22 height=%22200%22><filter id=%22n%22><feTurbulence baseFrequency=%220.9%22 numOctaves=%221%22 stitchTiles=%22stitch%22/></filter><rect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23n)%22/></svg>')" }}
      />

      {/* Floating decorative orbs — animated, blurred, add depth */}
      <motion.div
        aria-hidden
        className="absolute rounded-full pointer-events-none"
        initial={{ scale: 0.9, opacity: 0.5 }}
        animate={{ scale: [0.9, 1.05, 0.9], opacity: [0.5, 0.75, 0.5] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        style={{
          top: "10%", left: "-8%",
          width: 220, height: 220,
          background: `radial-gradient(circle, ${accent.from}55 0%, transparent 70%)`,
          filter: "blur(40px)",
        }}
      />
      <motion.div
        aria-hidden
        className="absolute rounded-full pointer-events-none"
        initial={{ scale: 1.05, opacity: 0.4 }}
        animate={{ scale: [1.05, 0.9, 1.05], opacity: [0.4, 0.65, 0.4] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        style={{
          bottom: "8%", right: "-6%",
          width: 280, height: 280,
          background: `radial-gradient(circle, ${accent.to}44 0%, transparent 70%)`,
          filter: "blur(50px)",
        }}
      />

      {/* Main layout — CSS grid guarantees true vertical centering */}
      <div
        className="relative z-10 h-full w-full"
        style={{
          display: "grid",
          gridTemplateRows: "auto 1fr auto",
          minHeight: "100dvh",
          paddingTop: "max(env(safe-area-inset-top, 0px), 20px)",
          paddingBottom: "max(env(safe-area-inset-bottom, 0px), 20px)",
        }}
      >
        {/* Row 1 — Rostr wordmark, centered, top */}
        <div className="flex flex-col items-center px-6 relative">
          {/* Language picker floats in corner, doesn't disturb centering */}
          <select
            data-testid="onboard-lang"
            value={lang}
            onChange={(e) => setLang(e.target.value)}
            className="absolute right-6 top-0 input-field !h-9 !text-[11px] !w-auto !rounded-full !bg-white/70 !backdrop-blur-md !border-white/60"
            style={{ boxShadow: "0 4px 12px -4px rgb(0 0 0 / 0.08)" }}
          >
            {langs.map((l) => (
              <option key={l.code} value={l.code}>{l.flag} {l.code.toUpperCase()}</option>
            ))}
          </select>

          <motion.div
            initial={{ y: -12, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
            className="font-display font-bold tracking-[-0.04em] leading-none text-center"
            style={{
              fontSize: "clamp(34px, 7vw, 44px)",
              backgroundImage: `linear-gradient(135deg, hsl(var(--text-strong)) 0%, ${accent.from} 60%, ${accent.to} 100%)`,
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
              paddingBottom: "2px",
            }}
          >
            Rostr
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.4 }}
            className="mt-1 text-[10px] uppercase tracking-[0.28em] font-bold text-[hsl(var(--text-muted))]"
          >
            {t("app_onboard_eyebrow")}
          </motion.div>
        </div>

        {/* Row 2 — Slide content, TRUE centering via grid + place-items-center */}
        <div className="grid place-items-center px-6 w-full">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, y: 20, filter: "blur(6px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: -20, filter: "blur(6px)" }}
              transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
              className="w-full max-w-[420px] flex flex-col items-center text-center"
            >
              {/* Halo around icon */}
              <div className="relative mb-9">
                <motion.div
                  aria-hidden
                  initial={{ scale: 0.6, opacity: 0 }}
                  animate={{ scale: [0.9, 1.08, 0.9], opacity: [0.4, 0.7, 0.4] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute inset-0 rounded-[40px]"
                  style={{
                    background: `linear-gradient(135deg, ${accent.from} 0%, ${accent.to} 100%)`,
                    filter: "blur(28px)",
                    transform: "scale(1.4)",
                  }}
                />
                <motion.div
                  initial={{ scale: 0.5, opacity: 0, rotate: -8 }}
                  animate={{ scale: 1, opacity: 1, rotate: 0 }}
                  transition={{ delay: 0.15, type: "spring", stiffness: 200, damping: 18 }}
                  className="relative w-32 h-32 rounded-[40px] grid place-items-center"
                  style={{
                    background: `linear-gradient(135deg, ${accent.from} 0%, ${accent.to} 100%)`,
                    boxShadow: `
                      0 30px 60px -20px ${accent.from}80,
                      0 10px 30px -12px ${accent.to}60,
                      inset 0 1px 0 rgb(255 255 255 / 0.4),
                      inset 0 -1px 0 rgb(0 0 0 / 0.1)
                    `,
                    color: "white",
                  }}
                >
                  <Icon size={52} strokeWidth={1.75} />
                </motion.div>
              </div>

              {/* Title */}
              <motion.h1
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.28, duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
                className="font-display font-bold tracking-[-0.035em] leading-[1.02] text-[hsl(var(--text-strong))]"
                style={{ fontSize: "clamp(32px, 8.5vw, 44px)" }}
                data-testid={`onboard-title-${step}`}
              >
                {t(slides[step].titleKey)}
              </motion.h1>

              {/* Description */}
              <motion.p
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.38, duration: 0.5 }}
                className="mt-5 text-[16px] leading-[1.55] text-[hsl(var(--text-muted))] max-w-[360px]"
              >
                {t(slides[step].descKey)}
              </motion.p>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Row 3 — Progress + actions, pinned to bottom */}
        <div className="w-full flex flex-col items-center gap-6 px-6 pt-4">
          {/* Progress dots */}
          <div className="flex items-center gap-2" data-testid="onboard-progress">
            {slides.map((_, i) => (
              <motion.span
                key={i}
                initial={{ backgroundColor: "hsl(var(--border))" }}
                animate={{
                  width: i === step ? 32 : 8,
                  backgroundColor: i === step ? accent.from : "hsl(var(--border))",
                  scale: i === step ? 1 : 0.9,
                }}
                transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
                className="h-2 rounded-full"
              />
            ))}
          </div>

          {/* Actions */}
          <div className="w-full max-w-[420px] flex items-center gap-3">
            <AnimatePresence>
              {step > 0 && (
                <motion.button
                  key="back"
                  initial={{ opacity: 0, x: -8, width: 0 }}
                  animate={{ opacity: 1, x: 0, width: 52 }}
                  exit={{ opacity: 0, x: -8, width: 0 }}
                  transition={{ duration: 0.3 }}
                  type="button"
                  onClick={back}
                  data-testid="onboard-back"
                  className="!h-13 !p-0 shrink-0 rounded-full border hairline bg-white/70 backdrop-blur-md hover:bg-white transition-colors grid place-items-center"
                  style={{ height: 52, boxShadow: "0 6px 16px -6px rgb(0 0 0 / 0.1)" }}
                  aria-label={t("back")}
                >
                  <ArrowLeft size={17} strokeWidth={2.2} className="text-[hsl(var(--text-strong))]" />
                </motion.button>
              )}
            </AnimatePresence>
            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={next}
              data-testid="onboard-next"
              layout
              className="group flex-1 relative overflow-hidden flex items-center justify-center gap-2 rounded-full text-white font-semibold text-[15.5px]"
              style={{
                height: 56,
                background: `linear-gradient(135deg, ${accent.from} 0%, ${accent.to} 100%)`,
                boxShadow: `
                  0 20px 44px -12px ${accent.from}80,
                  0 8px 20px -8px ${accent.to}60,
                  inset 0 1px 0 rgb(255 255 255 / 0.4)
                `,
              }}
            >
              {/* Sheen effect on hover */}
              <span
                aria-hidden
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
                style={{
                  background: "linear-gradient(115deg, transparent 30%, rgb(255 255 255 / 0.2) 50%, transparent 70%)",
                }}
              />
              <span className="relative z-10 flex items-center gap-2">
                {isLast ? t("app_onboard_start") : t("app_onboard_next")}
                <ArrowRight size={17} strokeWidth={2.4} className="group-hover:translate-x-0.5 transition-transform" />
              </span>
            </motion.button>
          </div>
        </div>
      </div>
    </div>
  );
}
